import useBodyScrollLock from '../hooks/useBodyScrollLock';
import React, { useState, useEffect, memo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import { RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";
import useSwipeNavigation from '../hooks/useSwipeNavigation';

const PackMediaModal = memo(({ show = false, onClose, mediaUrls = [], startIndex = 0 }) => {
    const [currentIndex, setCurrentIndex] = useState(startIndex);

    const goToPrevious = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? mediaUrls.length - 1 : prevIndex - 1));
    }, [mediaUrls.length]);

    const goToNext = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex === mediaUrls.length - 1 ? 0 : prevIndex + 1));
    }, [mediaUrls.length]);

    useBodyScrollLock(true);

    useEffect(() => {
        const handleKeys = (event) => {
            if (event.key === 'Escape') {
                // capture + stopImmediatePropagation: si este visor está montado
                // sobre otro modal (ej. el de perfil), Escape solo debe cerrar
                // este visor y no también el modal de abajo (mismo patrón que
                // usa FluidMediaModal2 para los packs).
                event.stopImmediatePropagation();
                onClose();
                return;
            }
            if (event.key === 'ArrowLeft') goToPrevious();
            if (event.key === 'ArrowRight') goToNext();
        };

        document.addEventListener('keydown', handleKeys, { capture: true });

        return () => {
            document.removeEventListener('keydown', handleKeys, { capture: true });
        };
    }, [onClose, goToNext, goToPrevious]);

    useEffect(() => {
        if (mediaUrls.length > 1) {
            const preloadNext = new Image();
            const preloadPrev = new Image();

            const nextIndex = (currentIndex + 1) % mediaUrls.length;
            const prevIndex = (currentIndex - 1 + mediaUrls.length) % mediaUrls.length;

            const nextUrl = typeof mediaUrls[nextIndex] === 'string' ? mediaUrls[nextIndex] : (mediaUrls[nextIndex]?.preview_file || mediaUrls[nextIndex]?.processed_file);
            const prevUrl = typeof mediaUrls[prevIndex] === 'string' ? mediaUrls[prevIndex] : (mediaUrls[prevIndex]?.preview_file || mediaUrls[prevIndex]?.processed_file);

            preloadNext.src = nextUrl;
            preloadPrev.src = prevUrl;
        }
    }, [currentIndex, mediaUrls]);

    const currentMediaUrl = mediaUrls[currentIndex];
    
    // Extract the correct URL based on file_type
    const getFileUrl = (media) => {
        if (!media) return null;
        
        if (typeof media === 'string') return media;
        
        // For videos, use processed_file (the full video)
        if (media?.file_type === 'video') {
            return media.processed_file || media.preview_file || media.legacy_url;
        }
        
        // For images, use processed_file first (the processed image), fallback to preview_file or legacy_url
        return media.processed_file || media.preview_file || media.legacy_url;
    };

    const currentMediaUrlString = getFileUrl(currentMediaUrl);

    // Swipe horizontal para navegar entre fotos, como se espera en un
    // visor móvil de Chrome/Safari (los botones de flecha quedan como
    // fallback para desktop/mouse).
    const { onTouchStart, onTouchEnd } = useSwipeNavigation({
        onSwipeLeft: goToNext,
        onSwipeRight: goToPrevious,
    });

    if (!show) return null;

    return createPortal(
        <div
            className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-80 overscroll-contain"
            style={{ zIndex: 999999 }}
            onClick={onClose}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            {mediaUrls.length > 1 && (
                <div
                    className="absolute left-1/2 transform -translate-x-1/2 bg-black bg-opacity-30 text-white px-4 py-2 rounded-full shadow-sm backdrop-blur-sm z-50 m-2"
                    style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
                >
                    <span className="font-medium">
                        {currentIndex + 1} / {mediaUrls.length}
                    </span>
                </div>
            )}

            <div className="relative w-full h-full max-w-4xl flex flex-col justify-center items-center p-4">
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="w-full h-full flex justify-center items-center"
                >
                    <div className="relative w-full h-full sm:max-w-full sm:max-h-dvh sm:rounded-xl flex justify-center items-center">
                        {currentMediaUrlString?.endsWith('.mp4') || currentMediaUrlString?.endsWith('.webm') ? (
                            <ReactPlayer
                                url={currentMediaUrlString}
                                controls={true}
                                playing={show}
                                width="100%"
                                height="100%"
                                className="w-full h-full"
                                config={{
                                    file: {
                                        attributes: {
                                            controlsList: 'nodownload',
                                        },
                                    },
                                }}
                                onError={(e) => {
                                    console.error('Error loading video:', e);
                                    console.error('Video URL:', currentMediaUrlString);
                                }}
                            />
                        ) : (
                            <img
                                src={currentMediaUrlString}
                                alt={`Media ${currentIndex}`}
                                className="max-w-full max-h-full object-contain sm:rounded-xl"
                                loading="lazy"
                                onError={(e) => {
                                    console.error('Error loading image:', e);
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>

            <button
                className="absolute right-4 m-2 bg-black bg-opacity-30 text-white p-2 rounded-full"
                style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
                onClick={(event) => {
                    // Sin esto el click también le llega al overlay de atrás (mismo
                    // onClick={onClose}) por bubbling, y como onClose ahora hace un
                    // history.goBack() real (no un simple setState), dispararlo dos
                    // veces de un solo click salta DOS entradas de historial en vez
                    // de una — cerrando también el modal de perfil de abajo.
                    event.stopPropagation();
                    onClose();
                }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                    />
                </svg>
            </button>

            {mediaUrls.length > 1 && (
                <>
                    {/* Botón para ir a la izquierda */}
                    <button
                        className="absolute top-1/2 transform -translate-y-1/2 m-1 bg-black bg-opacity-30 text-white p-1 rounded-full"
                        style={{ left: 'calc(1rem + env(safe-area-inset-left, 0px))' }}
                        onClick={(event) => {
                            goToPrevious();
                            event.stopPropagation();
                        }}
                    >
                        <RiArrowLeftSLine className="h-6 w-6 md:h-14 md:w-14" />
                    </button>

                    {/* Botón para ir a la derecha */}
                    <button
                        className="absolute top-1/2 transform -translate-y-1/2 m-1 bg-black bg-opacity-30 text-white p-1 rounded-full"
                        style={{ right: 'calc(1rem + env(safe-area-inset-right, 0px))' }}
                        onClick={(event) => {
                            goToNext();
                            event.stopPropagation();
                        }}
                    >
                        <RiArrowRightSLine className="h-6 w-6 md:h-14 md:w-14" />
                    </button>
                </>
            )}
        </div>,
        document.body
    );
});

PackMediaModal.propTypes = {
    show: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    mediaUrls: PropTypes.array,
    startIndex: PropTypes.number,
};

export default PackMediaModal;
